import React, { ReactNode, useEffect, useState } from "react";
import { workbookCache } from "../../../excel/workbook/cache";
import { BodyText } from "../text/BodyText.styled";
import {
  Accordion,
  AccordionDetails,
  AccordionExpandIcon,
  AccordionSummary,
  AccordionTitle,
  DetailRow,
  DetailValue,
  DetailsList,
  LastUpdatedText,
} from "./Accordion.styled";
import dayjs from "dayjs";
import { UiStateCacheKey } from "../../../excel/workbook/cacheKeys";

interface Detail {
  label: string;
  value: string;
}

interface AccordionLayoutProps {
  cacheKey: UiStateCacheKey;
  title?: string;
  details?: Detail[];
  lastUpdated?: string;
  lastUpdatedLabel?: string;
  lastUpdatedFormat?: string;
  children?: ReactNode;
}

const expandedMemo = new Map<string, boolean>();

export const accordionExpandedCacheKey = (type: string): UiStateCacheKey => `cardDetailsExpanded_${type}`;

const AccordionLayout = ({
  cacheKey,
  title = "Details",
  details,
  lastUpdated,
  lastUpdatedLabel = "Last Updated",
  lastUpdatedFormat = "MM/DD/YYYY HH:mm",
  children,
}: AccordionLayoutProps) => {
  const [expanded, setExpanded] = useState<boolean>(() => expandedMemo.get(cacheKey) ?? false);

  useEffect(() => {
    let isCurrent = true;

    const restore = async () => {
      const cached = await workbookCache.get(cacheKey);
      if (!isCurrent || typeof cached !== "boolean") return;
      expandedMemo.set(cacheKey, cached);
      setExpanded(cached);
    };

    restore();

    return () => {
      isCurrent = false;
    };
  }, [cacheKey]);

  const handleToggle = (_event: React.SyntheticEvent, isExpanded: boolean) => {
    expandedMemo.set(cacheKey, isExpanded);
    setExpanded(isExpanded);
    void workbookCache.set({ [cacheKey]: isExpanded });
  };

  return (
    <Accordion disableGutters expanded={expanded} onChange={handleToggle}>
      <AccordionSummary expandIcon={<AccordionExpandIcon />}>
        <AccordionTitle>{title}</AccordionTitle>
      </AccordionSummary>
      <AccordionDetails>
        <DetailsList>
          {details?.map((detail, index) => (
            <DetailRow key={index}>
              <BodyText>{detail.label}</BodyText>
              <DetailValue $emphasis={index === 0}>{detail.value}</DetailValue>
            </DetailRow>
          ))}
        </DetailsList>
        {children}
        {lastUpdated && (
          <LastUpdatedText>
            {lastUpdatedLabel}: {dayjs(lastUpdated).format(lastUpdatedFormat)}
          </LastUpdatedText>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default AccordionLayout;
